import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@klonup/design-system';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';

/**
 * ProtectedRoute (Guard)
 * Protege las rutas privadas de Klonup asegurando que exista una sesión válida.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoggedIn, jwtToken, isLoading } = useAuth();
  const isAuthReady = useAuthStore((s) => s.isAuthReady);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  // Esperar a que Firebase resuelva el estado inicial (evita llamadas a API sin token)
  if (!isAuthReady || isLoading) {
    return (
      <div className="min-h-screen bg-klonup-light flex items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Si no hay sesión, redirigimos al login
  if (!isLoggedIn || !jwtToken) {
    /**
     * state={{ from: location }}: Guardamos de dónde venía el usuario.
     * Así, después de loguearse, lo podemos devolver a la página exacta
     * donde intentó entrar (ej. /connections/123).
     */
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Usuario inactivo: redirigir a pantalla de cuenta desactivada
  if (user?.status === 'inactive') {
    return <Navigate to="/inactive" replace />;
  }

  // ✅ Si está autenticado y activo, permitimos el acceso
  return <>{children}</>;
};

export default ProtectedRoute;